// material-ui
import { Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";

// project imports
import NavGroup from "./NavGroup";

import {
  Album,
  CampaignOutlined,
  Download,
  DvrOutlined,
  GppGood,
  Groups,
  GraphicEqOutlined,
  VideoCameraFront,
  EmojiSymbols,
  Groups2,
  SensorsOutlined,
  MovieFilterOutlined,
  ReportProblemOutlined,
  InsightsOutlined,
  GridViewOutlined,
  QueryStatsOutlined,
  TuneOutlined,
  LabelOutlined,
  SwapHorizOutlined,
  SubscriptionsOutlined,
  RateReviewOutlined,
  HowToRegOutlined,
  VideoFileOutlined,
  HistoryOutlined,
  EventOutlined,
  FlagOutlined,
  LiveTvOutlined,
  VideoLibraryOutlined,
  CalendarMonthOutlined,
  SettingsInputAntennaOutlined,
} from "@mui/icons-material";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import ViewTimelineOutlined from "@mui/icons-material/ViewTimelineOutlined";
import { IconDashboard, IconMovie, IconUser, IconUserExclamation } from "@tabler/icons";

import { UserContext } from "src/state/userState";

// ==============================|| SIDEBAR MENU LIST ||============================== //
const MenuList = () => {
  const { contactUsCount, userAccessPages } = useContext(UserContext);
  const [menuItem, setMenuItem] = useState(false);

  useEffect(() => {
    setMenuItem(getMenuItem(userAccessPages));
  }, [contactUsCount, userAccessPages]);

  if (!menuItem) return false;

  return menuItem.map((item) => {
    if (!item) return false;

    switch (item.type) {
      case "group":
        return (
          <NavGroup
            key={item.id}
            item={item}
          />
        );
      default:
        return (
          <Typography
            key={item.id}
            variant="h6"
            color="error"
            align="center"
          >
            Menu Items Error
          </Typography>
        );
    }
  });
};

export default MenuList;

export function getMenuItem(pages) {
  const map = {};
  pages.forEach((page) => {
    if (page.write || page.read) map[page.page_id] = true;
  });

  const returnVal = [];
  if (map.dashboard || map.reports || map.klooma_subscription) {
    returnVal.push({
      id: "dashboards",
      title: "Dashboards",
      type: "group",
      breadcrumb: false,
      children: [
        map.dashboard && {
          id: "/",
          title: "General Dashboard",
          type: "item",
          icon: IconDashboard,
          url: "/",
        },
        // Overview/Content/Revenue/Integrity, guarded by the same `reports`
        // ability as the General Dashboard and Content Dashboard routes.
        map.reports && {
          id: "analytics",
          title: "Analytics & charts",
          type: "item",
          icon: InsightsOutlined,
          url: "/analytics",
        },
        // Klooma Premium's dashboard, a sibling of the other dashboards
        // rather than a section of its own.
        map.klooma_subscription && {
          id: "klooma-subscription-dashboard",
          title: "Klooma Subscription",
          type: "collapse",
          icon: SubscriptionsOutlined,
          breadcrumb: false,
          children: [
            {
              id: "klooma-subscription-overview",
              title: "Overview",
              type: "item",
              url: "/klooma-subscription/overview",
              icon: SubscriptionsOutlined,
              breadcrumb: false,
            },
            {
              id: "klooma-subscription-growth",
              title: "Growth & Retention",
              type: "item",
              url: "/klooma-subscription/growth",
              icon: SubscriptionsOutlined,
              breadcrumb: false,
            },
            {
              id: "klooma-subscription-activity",
              title: "Activity",
              type: "item",
              url: "/klooma-subscription/activity",
              icon: SubscriptionsOutlined,
              breadcrumb: false,
            },
            {
              id: "klooma-subscription-members",
              title: "Members",
              type: "item",
              url: "/klooma-subscription/members",
              icon: SubscriptionsOutlined,
              breadcrumb: false,
            },
          ],
        },
      ],
    });
  }
  if (pages.some((page) => page.page_id === "configurations" && page.read)) {
    returnVal.push({ id: "hero-editor", title: "Featured", type: "group", children: [
      { id: "game-editor", title: "Games presentation", type: "item", url: "/games-editor", icon: MovieFilterOutlined, breadcrumb: false },
      { id: "hero-sliders", title: "Hero sliders", type: "item", url: "/hero-editor", icon: MovieFilterOutlined, breadcrumb: false },
    ] });
  }
  const canReadMovies = pages.some((page) => page.page_id === "movies" && page.read);
  const canReadTv = pages.some((page) => page.page_id === "series" && page.read);
  if (canReadMovies || canReadTv) {
    returnVal.push({
      id: "discover", title: "Discover", type: "group",
      children: [
        canReadMovies && { id: "discover-movies", title: "Featured movies", type: "item", url: "/discover/movies", icon: MovieFilterOutlined, breadcrumb: false },
        canReadTv && { id: "discover-tv", title: "Featured TV shows", type: "item", url: "/discover/tv", icon: DvrOutlined, breadcrumb: false },
      ].filter(Boolean),
    });
  }
  // Sync rooms, Lives and the Live schedule are one area of the panel.
  const canReadSyncRooms = pages.some((page) => page.page_id === "sync_rooms" && page.read);
  const canReadLives = pages.some((page) => page.page_id === "lives" && page.read);
  // One "Live schedule" for creator Lives and Klooma events. An admin who only
  // holds the legacy `live` page keeps a door to the events list.
  const canReadSchedule = pages.some((page) => page.page_id === "scheduled_lives" && page.read);
  const canReadEventsOnly = !canReadSchedule && pages.some((page) => page.page_id === "live" && page.read);
  // Reports from Sync rooms and Lives: Moderation plus one of the two room pages.
  const canReadRoomReports =
    pages.some((page) => page.page_id === "moderation" && page.read) && (canReadSyncRooms || canReadLives);
  const canReadOverview = pages.some((page) => page.page_id === "rooms_overview" && page.read);
  const canReadLiveAnalytics = pages.some((page) => page.page_id === "live_sync_analytics" && page.read);
  if (canReadSyncRooms || canReadLives || canReadSchedule || canReadEventsOnly || canReadOverview || canReadLiveAnalytics) {
    returnVal.push({
      id: "sync-operations",
      title: "Live & Sync",
      type: "group",
      children: [
        canReadOverview && { id: "rooms-overview", title: "Rooms overview", type: "item", url: "/rooms-overview", icon: GridViewOutlined, breadcrumb: false },
        canReadLiveAnalytics && { id: "live-sync-analytics", title: "Live & Sync analytics", type: "item", url: "/live-sync-analytics", icon: QueryStatsOutlined, breadcrumb: false },
        canReadSyncRooms && { id: "sync-rooms", title: "Sync rooms", type: "item", url: "/sync-rooms", icon: Groups, breadcrumb: false },
        canReadLives && { id: "lives", title: "Lives", type: "item", url: "/lives", icon: SensorsOutlined, breadcrumb: false },
        canReadRoomReports && { id: "room-reports", title: "Room reports", type: "item", url: "/room-reports", icon: FlagOutlined, breadcrumb: false },
        canReadSchedule && { id: "live-schedule", title: "Live schedule", type: "item", url: "/live-schedule", icon: EventOutlined, breadcrumb: false },
        canReadEventsOnly && { id: "live-content", title: "Klooma events", type: "item", url: "/live", icon: VideoCameraFront, breadcrumb: false },
      ].filter(Boolean),
    });
  }
  // FAST channels, right after Live & Sync. Later channel pages (feeds,
  // schedules, the guide) join this group as their own items.
  const canReadChannels = pages.some((page) => page.page_id === "channels" && page.read);
  const canReadLibrary = pages.some((page) => page.page_id === "content_library" && page.read);
  const canReadChannelSchedule = pages.some((page) => page.page_id === "channel_schedule" && page.read);
  const canReadChannelAnalytics = pages.some((page) => page.page_id === "channel_analytics" && page.read);
  const canReadPlayout = pages.some((page) => page.page_id === "playout" && page.read);
  const canReadProgramGuide = pages.some((page) => page.page_id === "program_guide" && page.read);
  if (canReadChannels || canReadLibrary || canReadChannelSchedule || canReadPlayout || canReadChannelAnalytics || canReadProgramGuide) {
    returnVal.push({
      id: "channels",
      title: "Channels",
      type: "group",
      children: [
        canReadChannels && { id: "fast-channels", title: "Channels", type: "item", url: "/channels", icon: LiveTvOutlined, breadcrumb: false },
        canReadLibrary && { id: "content-library", title: "Content Library", type: "item", url: "/content-library", icon: VideoLibraryOutlined, breadcrumb: false },
        canReadChannelSchedule && { id: "channel-schedule", title: "Schedule", type: "item", url: "/schedule", icon: CalendarMonthOutlined, breadcrumb: false },
        canReadPlayout && { id: "fast-playout", title: "Playout", type: "item", url: "/playout", icon: SettingsInputAntennaOutlined, breadcrumb: false },
        canReadProgramGuide && { id: "program-guide", title: "Program Guide", type: "item", url: "/program-guide", icon: ViewTimelineOutlined, breadcrumb: false },
        canReadChannelAnalytics && { id: "channel-analytics", title: "Analytics", type: "item", url: "/channel-analytics", icon: InsightsOutlined, breadcrumb: false },
      ].filter(Boolean),
    });
  }
  returnVal.push({
    id: "music",
    title: "Music Content",
    type: "group",
    children: [
      // The music record's Releases screen (authoring.md) - first in the
      // list, its "default landing" per the design.
      map.music && {
        id: "music-releases",
        title: "Releases",
        type: "item",
        url: "/music/releases",
        icon: Album,
        breadcrumb: false,
      },
      // The Songs screen (authoring.md §2) - right after Releases. The
      // legacy "Music" item (song CRUD on the old tables) was removed in
      // batch B7.
      map.music && {
        id: "music-songs",
        title: "Songs",
        type: "item",
        url: "/music/songs",
        icon: GraphicEqOutlined,
        breadcrumb: false,
      },
      // The Music videos screen (authoring.md §3) - right after Songs. The
      // legacy "Music Video" item (video CRUD on the old tables) was
      // removed in batch B7.
      map.music_video && {
        id: "music-videos",
        title: "Music Videos",
        type: "item",
        url: "/music/videos",
        icon: VideoCameraFront,
        breadcrumb: false,
      },
      // Labels and Genre mapping (authoring.md §4/§5) - right after the
      // record's Releases/Songs/Music Videos, per the nav order in
      // authoring.md's own "Navigation" section.
      map.labels && {
        id: "music-labels",
        title: "Labels",
        type: "item",
        url: "/music/labels",
        icon: LabelOutlined,
        breadcrumb: false,
      },
      map.music && {
        id: "music-genre-mapping",
        title: "Genre mapping",
        type: "item",
        url: "/music/genre-mapping",
        icon: SwapHorizOutlined,
        breadcrumb: false,
      },
    ],
  });

  if (map.movies || map.series || map.podcast) {
    returnVal.push({
      id: "conentManagement",
      title: "Content Management",
      type: "group",
      children: [
        map.movies && {
          id: "movies",
          title: "Movies",
          type: "item",
          url: "/movies",
          icon: IconMovie,
          breadcrumb: false,
        },
        map.series && {
          id: "series",
          title: "TV Shows",
          type: "item",
          url: "/series",
          icon: DvrOutlined,
          breadcrumb: false,
        },
        map.podcast && {
          id: "podcast",
          title: "Podcasts",
          type: "item",
          url: "/podcast",
          icon: PodcastsIcon,
          breadcrumb: false,
        },
        {
          id: "featured-content",
          title: "Featured Content",
          type: "item",
          url: "/featured-content",
          icon: EmojiSymbols,
          breadcrumb: false,
        },
      ],
    });
  }

  if (map.customers || map.admins || map.roles || map.audit_log) {
    returnVal.push({
      id: "userManagement",
      title: "User Management",
      type: "group",
      children: [
        // One list; the type is a filter on it (REPORT §7.2), not a menu.
        map.customers && {
          id: "users",
          title: "Users",
          type: "item",
          url: "/users",
          icon: IconUser,
          breadcrumb: false,
        },
        map.admins && {
          id: "admins",
          title: "Admins",
          type: "item",
          url: "/admins",
          icon: GppGood,
          breadcrumb: false,
        },
        map.roles && {
          id: "roles",
          title: "Roles",
          type: "item",
          url: "/roles",
          icon: Groups,
          breadcrumb: false,
        },
        // Who did what in the panel sits with who can do it.
        map.audit_log && {
          id: "audit-log",
          title: "Audit log",
          type: "item",
          url: "/audit-log",
          icon: HistoryOutlined,
          breadcrumb: false,
        },
      ],
    });
  }

  if (map.fan_club || map.customers) {
    returnVal.push({
      id: "fanClub",
      title: "Fan Club",
      type: "group",
      children: [
        map.fan_club && {
          id: "fan-club-dashboard",
          title: "Dashboard",
          type: "collapse",
          icon: InsightsOutlined,
          breadcrumb: false,
          children: [
            {
              id: "fan-club-overview",
              title: "Overview",
              type: "item",
              url: "/fan-club/overview",
              icon: InsightsOutlined,
              breadcrumb: false,
            },
            {
              id: "fan-club-growth",
              title: "Growth & Retention",
              type: "item",
              url: "/fan-club/growth",
              icon: InsightsOutlined,
              breadcrumb: false,
            },
            {
              id: "fan-club-activity",
              title: "Activity",
              type: "item",
              url: "/fan-club/activity",
              icon: InsightsOutlined,
              breadcrumb: false,
            },
            {
              id: "fan-club-top-creators",
              title: "Top Creators",
              type: "item",
              url: "/fan-club/top-creators",
              icon: InsightsOutlined,
              breadcrumb: false,
            },
          ],
        },
        map.customers && {
          id: "fan-club-featured",
          title: "Fan Clubs Banner",
          type: "item",
          url: "/fan-club-featured",
          icon: Groups2,
          breadcrumb: false,
        },
      ],
    });
  }

  if (map.settings || map.translations || map.faq || map.configurations || map.subscription) {
    returnVal.push({
      id: "applicationSettings",
      title: "Settings",
      type: "group",
      children: [
        // One entry for the whole section; the tabs are inside it.
        (map.settings || map.configurations) && {
          id: "settings",
          title: "Settings",
          type: "item",
          url: map.settings ? "/settings" : "/settings/configurations",
          icon: TuneOutlined,
          breadcrumb: false,
        },
      ],
    });
  }

  if (map.media_jobs) {
    returnVal.push({
      id: "media",
      title: "Media",
      type: "group",
      children: [
        {
          id: "media-jobs",
          title: "Media Jobs",
          type: "item",
          url: "/media-jobs",
          icon: MovieFilterOutlined,
          breadcrumb: false,
        },
      ],
    });
  }

  const canReadAdsInquiries = pages.some((page) => page.page_id === "ads_inquiries" && page.read);
  const canReviewAdsCreative = pages.some((page) => page.page_id === "ads_review" && page.write);
  const canReconcileAdsFinance = pages.some((page) => page.page_id === "ads_finance" && page.write);
  const canReviewPlayables = process.env.REACT_APP_ADS_PLAYABLE_REVIEW_ENABLED === "true"
    && pages.some((page) => (page.page_id === "ads_review" || page.page_id === "ads_operations") && page.write);
  const canReadAdsBilling = process.env.REACT_APP_ADS_BILLING_STAFF_ENABLED === "true"
    && pages.some((page) => page.page_id === "ads_finance" && (page.read || page.write));
  const canReadProviderOps = pages.some((page) => page.page_id === "ads_provider_ops" && page.read);
  const canReadAdsSettings = process.env.REACT_APP_ADS_STAFF_SETTINGS_ENABLED === "true"
    && pages.some((page) => (page.page_id === "ads_finance" || page.page_id === "ads_operations") && (page.read || page.write));
  const canReadRevenueShare = process.env.REACT_APP_ADS_REVENUE_SHARE_ENABLED === "true"
    && pages.some((page) => page.page_id === "ads_finance" && (page.read || page.write));
  const canOperateAds = pages.some((page) => page.page_id === "ads_operations" && page.write);
  const teamAccessStaff = process.env.REACT_APP_ADS_TEAM_ACCESS_STAFF_ENABLED === "true";
  const canReadTeamAccess = teamAccessStaff && pages.some((page) => page.page_id === "ads_operations" && (page.read || page.write));
  const canReadAccountSecurity = teamAccessStaff && pages.some((page) => page.page_id === "ads_account_security" && (page.read || page.write));
  const canReinstateAds = pages.some((page) => page.page_id === "ads_policy" && page.write) && process.env.REACT_APP_FAST_AD_AIR_REINSTATE_ENABLED === "true";
  if (canReadAdsInquiries || canReviewAdsCreative || canReadProviderOps || canReconcileAdsFinance || canOperateAds || canReinstateAds || canReadAdsBilling || canReviewPlayables || canReadAdsSettings || canReadRevenueShare || canReadTeamAccess || canReadAccountSecurity) {
    returnVal.push({
      id: "ads",
      title: "Ads",
      type: "group",
      children: [
        canReadProviderOps && { id: "ads-overview", title: "Overview", type: "item", url: "/ads/overview", icon: GridViewOutlined, breadcrumb: false },
        canReadAdsInquiries && { id: "ads-inquiries", title: "Ad inquiries", type: "item", url: "/ads/inquiries", icon: CampaignOutlined, breadcrumb: false },
        canReviewAdsCreative && { id: "ads-account-reviews", title: "Account reviews", type: "item", url: "/ads/account-reviews", icon: HowToRegOutlined, breadcrumb: false },
        canReviewAdsCreative && { id: "ads-asset-reviews", title: "Ad image reviews", type: "item", url: "/ads/asset-reviews", icon: VideoFileOutlined, breadcrumb: false },
        canReviewAdsCreative && { id: "ads-creative-reviews", title: "Creative reviews", type: "item", url: "/ads/creative-reviews", icon: RateReviewOutlined, breadcrumb: false },
        canReviewAdsCreative && { id: "ads-social-reports", title: "Social ad reports", type: "item", url: "/ads/social-reports", icon: RateReviewOutlined, breadcrumb: false },
        canReviewAdsCreative && { id: "ads-verification", title: "Verification cases", type: "item", url: "/ads/verification", icon: RateReviewOutlined, breadcrumb: false },
        canReadProviderOps && { id: "ads-operations", title: "Operations", type: "item", url: "/ads/operations", icon: TuneOutlined, breadcrumb: false },
        canOperateAds && process.env.REACT_APP_ADS_SOCIAL_POLICY_STAFF_ENABLED === "true" && { id: "ads-social-policy", title: "Social ad spacing", type: "item", url: "/ads/social-policy", icon: TuneOutlined, breadcrumb: false },
        canOperateAds && process.env.REACT_APP_ADS_RUNTIME_SWITCHES_ENABLED === "true" && { id: "ads-feature-switches", title: "Feature switches", type: "item", url: "/ads/feature-switches", icon: TuneOutlined, breadcrumb: false },
        canOperateAds && { id: "ads-fan-club-sponsorships", title: "Fan Club sponsorships", type: "item", url: "/ads/fan-club-sponsorships", icon: CampaignOutlined, breadcrumb: false },
        canOperateAds && process.env.REACT_APP_FAST_AD_AIR_ENABLED === "true" && { id: "ads-fast-ad-air", title: "FAST ad air", type: "item", url: "/ads/fast-ad-air", icon: CampaignOutlined, breadcrumb: false },
        canReinstateAds && { id: "ads-fast-ad-air-reinstate", title: "Reinstate FAST ads", type: "item", url: "/ads/fast-ad-air/reinstate", icon: RateReviewOutlined, breadcrumb: false },
        canReconcileAdsFinance && { id: "ads-finance-reconciliation", title: "Finance preview", type: "item", url: "/ads/finance/reconciliation", icon: QueryStatsOutlined, breadcrumb: false },
        canReadAdsBilling && { id: "ads-billing", title: "Billing", type: "item", url: "/ads/billing", icon: QueryStatsOutlined, breadcrumb: false },
        process.env.REACT_APP_ADS_PODCAST_STAFF_ENABLED === "true" && pages.some((page) => (page.page_id === "ads_review" || page.page_id === "ads_operations") && page.write)
          && { id: "ads-podcast", title: "Podcast ads", type: "item", url: "/ads/podcast", icon: RateReviewOutlined, breadcrumb: false },
        canReviewPlayables && { id: "ads-playables", title: "Playable reviews", type: "item", url: "/ads/playables", icon: RateReviewOutlined, breadcrumb: false },
        canReadRevenueShare && { id: "ads-revenue-share", title: "Revenue share", type: "item", url: "/ads/revenue-share", icon: QueryStatsOutlined, breadcrumb: false },
        canReadAdsSettings && { id: "ads-settings", title: "Settings", type: "item", url: "/ads/settings", icon: TuneOutlined, breadcrumb: false },
        canReadAdsSettings && { id: "ads-approvals", title: "Approvals", type: "item", url: "/ads/approvals", icon: HowToRegOutlined, breadcrumb: false },
        canReadTeamAccess && { id: "ads-team-access", title: "Team access", type: "item", url: "/ads/team-access", icon: HowToRegOutlined, breadcrumb: false },
        canReadAccountSecurity && { id: "ads-mfa-resets", title: "Authenticator resets", type: "item", url: "/ads/mfa-resets", icon: HowToRegOutlined, breadcrumb: false },
      ].filter(Boolean),
    });
  }

  if (map.moderation || map.podcast_review || map.podcaster_applications || map.fan_letter_review) {
    returnVal.push({
      id: "moderation",
      title: "Moderation",
      type: "group",
      children: [
        map.moderation && {
          id: "moderation-queue",
          title: "Reports",
          type: "item",
          url: "/moderation",
          icon: ReportProblemOutlined,
          breadcrumb: false,
        },
        // Member podcast submissions waiting for a decision (PRD §3.3).
        map.podcast_review && {
          id: "podcast-review",
          title: "Podcast review",
          type: "item",
          url: "/podcast-review",
          icon: RateReviewOutlined,
          breadcrumb: false,
        },
        // Video fan letters held until a person or the screener clears them.
        map.fan_letter_review && {
          id: "fan-letter-review",
          title: "Fan letter review",
          type: "item",
          url: "/fan-letter-review",
          icon: VideoFileOutlined,
          breadcrumb: false,
        },
        map.podcaster_applications && {
          id: "podcaster-applications",
          title: "Podcaster applications",
          type: "item",
          url: "/podcaster-applications",
          icon: HowToRegOutlined,
          breadcrumb: false,
        },
      ],
    });
  }

  returnVal.push({
    id: "transactions",
    title: "Transactions",
    type: "group",
    children: [
      true && {
        id: "sony-ingestion",
        title: "Sony Ingestion",
        type: "item",
        url: "/sony-ingestion",
        icon: Download,
        breadcrumb: false,
      },
      // map.settings && {
      //   id: "general-settings",
      //   title: "General",
      //   type: "collapse",
      //   icon: IconSettings,
      //   breadcrumb: false,
      //   children: [
      //   ],
      // },
    ],
  });

  // Standalone final destination, after every content/settings/transaction group.
  // Keep the existing configurations permission and route unchanged.
  if (map.configurations) {
    returnVal.push({
      id: "systemStatus",
      type: "group",
      children: [{
        id: "system-status",
        title: "System status",
        type: "item",
        url: "/system-status",
        icon: InsightsOutlined,
        breadcrumb: false,
      }],
    });
  }

  return returnVal;
}
